import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { Node } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { isEmpty } from 'lodash';
import { DECORATION_CLASS, HEX_DECORATION_CLASS } from './inlineSwatchTypes';
import { inlineSwatchClickHandler } from './inlineSwatchClickHandler';
import { inlineSwatchDragHandler } from './inlineSwatchDragHandler';
import platformSingleton from '../../../../client/platform/platformSingleton';
import { DeviceFormFactor } from '../../../platform/platformTypes';

// Disable the decorations once the document is larger than this size (~9k words)
//  to avoid performance issues
const COLOR_PREVIEW_DOC_SIZE_LIMIT = 50000;

// These regexes aren't perfect (e.g. RGB must be between 0-255, etc.), but close enough
const HEX_REGEX = '(#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8}))';
const RGB_HSL_REGEX = '(rgb|hsl)a?\\(\\d{1,3}%?(,?\\s?\\d{1,3}%?){2}(,?\\s?\\/?\\s?)(1|0?\\.\\d+)?\\)';

// Disabling lab/lch for now, as it's not widely used and our colour swatch cards don't currently support it.
//  So the experience was inconsistent when dragging and dropping the colours to the canvas
// const LAB_LCH_REGEX =
//     '(lab|lch|oklab|oklch)\\(\\d{1,3}\\.?\\d{0,4}?%?(\\s-?\\d{1,3}\\.?\\d{0,4}?){2}(\\s\\/\\s\\d{1}\\.?\\d{1,2})?\\)';

// NOTE: This regex takes ~1 - 1.5ms to run on a 72k word document
const COLOR_REGEX = new RegExp(`(${HEX_REGEX}|${RGB_HSL_REGEX})(?=\\s|$)`, 'gi');

type InlineSwatchPluginState = {
    hasSkippedDecoration: boolean;
    decorations: DecorationSet;
} | null;

/**
 * See if there's an existing decoration for the given range.
 */
const hasExistingDecoration = (prevPluginState: InlineSwatchPluginState | undefined, from: number, to: number) =>
    !isEmpty(prevPluginState?.decorations.find(from, to));

/**
 * The decoration should be skipped if the user is currently creating the text and there's not an existing
 * a decoration for it.
 *
 * If there's not currently a selection, we're either not editing the document or the editor is not focused,
 * so we should not skip the decoration.
 */
const shouldSkipDecoration = (
    selection: Selection | undefined,
    from: number,
    to: number,
    prevPluginState: InlineSwatchPluginState | undefined,
): boolean => {
    if (!selection) return false;

    // If there's an existing decoration for this range, we should not skip it
    if (hasExistingDecoration(prevPluginState, from, to)) return false;

    // Also don't create the decoration if we're editing inside the range (and it's not currently created)
    if (selection.anchor >= from && selection.anchor <= to) return true;

    // Otherwise, render the decoration
    return false;
};

/**
 * Searches through all the nodes of a document to find text that matches an inline colour pattern
 * and wraps it in a decoration span.
 */
const findTextColors = (
    doc: Node,
    selection?: Selection,
    prevPluginState?: InlineSwatchPluginState,
): InlineSwatchPluginState => {
    if (doc.nodeSize > COLOR_PREVIEW_DOC_SIZE_LIMIT) return null;

    let hasSkippedDecoration = false;
    const decorations: Decoration[] = [];

    doc.descendants((node, position) => {
        if (!node.text) return;

        const matches = Array.from(node.text.matchAll(COLOR_REGEX));

        for (const match of matches) {
            const color = match[0];
            const index = match.index || 0;
            const from = position + index;
            const to = from + color.length;

            const shouldSkip = shouldSkipDecoration(selection, from, to, prevPluginState);

            hasSkippedDecoration = hasSkippedDecoration || shouldSkip;

            if (shouldSkip) continue;

            const isHex = color.startsWith('#');
            const className = isHex ? HEX_DECORATION_CLASS : DECORATION_CLASS;

            const decoration = Decoration.inline(from, to, {
                class: className,
                style: `--color: ${color}`,
                'data-from': String(from),
                'data-to': String(to),
                draggable: platformSingleton.formFactor === DeviceFormFactor.desktop ? 'true' : 'false',
            });

            decorations.push(decoration);
        }
    });

    return {
        hasSkippedDecoration,
        decorations: DecorationSet.create(doc, decorations),
    };
};

const INLINE_SWATCH_PLUGIN_KEY = 'inlineSwatch';

type InlineSwatchOptions = {
    isEditable?: boolean;
};

/**
 * Finds colour codes within text and decorates them with a span element.
 * CSS styles are then applied to show the colour next to the text.
 */
export const InlineSwatch = Extension.create<InlineSwatchOptions>({
    name: INLINE_SWATCH_PLUGIN_KEY,

    addProseMirrorPlugins() {
        const { editor } = this;
        return [
            new Plugin({
                key: new PluginKey(INLINE_SWATCH_PLUGIN_KEY),
                state: {
                    init(_, { doc }) {
                        return findTextColors(doc);
                    },
                    apply: (transaction, prevPluginState) => {
                        // If we've skipped a decoration, then we want to check for text colours again, even
                        // if the document hasn't changed, in case the selection has moved out of the decoration range.
                        if (!transaction.docChanged && !prevPluginState?.hasSkippedDecoration) return prevPluginState;

                        return findTextColors(
                            transaction.doc,
                            // Don't worry about the current selection if the editor isn't focused,
                            //  we simply want to match all colours in that case.
                            editor.isFocused ? transaction.selection : undefined,
                            prevPluginState,
                        );
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state)?.decorations;
                    },
                },
            }),
            this.options.isEditable ? inlineSwatchClickHandler(editor) : undefined,
            this.options.isEditable ? inlineSwatchDragHandler() : undefined,
        ].filter(Boolean) as Plugin<any>[];
    },
});
